import { apiClient } from 'lib/api';
import { useMutation } from '@tanstack/react-query';
import type { MutationConfig } from 'lib/react-query';
import { isAxiosError } from 'axios';
import type { AdSetEntry } from './getAdSets';
import type { CreativePackEntry, PartnershipAdSettingsPayload } from '../types';

export interface CreatePartnershipAdsParams {
  app?: string;
  platform?: string;
  creative_packs: CreativePackEntry[];
  ad_sets: AdSetEntry[] | null;
  call_to_action?: string;
  hotjar?: boolean;
  conversion_domain?: string | null;
  optional_ad_label?: string;
  visual_touch_ups?: boolean;
  hypothesis?: string;
  scaling_flows?: null;
  source?: string;
  partnership_ad_settings: PartnershipAdSettingsPayload;
}

export interface CreatePartnershipAdsResponse {
  job_id: string;
}

const createPartnershipAds = async (
  body: CreatePartnershipAdsParams,
): Promise<CreatePartnershipAdsResponse> => {
  const res = await apiClient.post<CreatePartnershipAdsResponse>(
    '/api/v1/facebook_manager/create_partnerships_ads',
    body,
  );

  if (isAxiosError(res)) {
    const error = new Error('Create Partnership Ads Error');
    error.cause = {
      ...res.response?.data,
      status: res.response?.status,
    };
    return Promise.reject(error);
  }
  return res.data;
};

const createPartnershipAdsLocal = (
  body: CreatePartnershipAdsParams,
): Promise<CreatePartnershipAdsResponse> => {
  console.log('[DEV] create_partnerships_ads payload:', body);
  return new Promise((resolve) => {
    resolve({ job_id: 'mock-partnership-job-id' });
  });
};

const submitPartnershipAds =
  process.env.NODE_ENV === 'development'
    ? createPartnershipAdsLocal
    : createPartnershipAds;

interface UseCreatePartnershipAdsOptions {
  config?: MutationConfig<typeof submitPartnershipAds>;
}

const useCreatePartnershipAds = ({ config }: UseCreatePartnershipAdsOptions = {}) => {
  return useMutation({
    ...config,
    onSuccess: (...args) => {
      if (config?.onSuccess) {
        config.onSuccess(...args);
      }
    },
    onError: (...args) => {
      if (config?.onError) {
        config.onError(...args);
      }
    },
    mutationFn: submitPartnershipAds,
  });
};

export { createPartnershipAds, useCreatePartnershipAds };
